import { useTranslation } from 'react-i18next';

import type { FinancialStatus, ShipmentStatusGroup } from '@/api/types/common';
import { eventCodeKey } from '@/lib/tracking-event-codes';
import { cn } from '@/lib/utils';

/** Figma-derived badge tints, one per colour-coding group. */
const GROUP_BG: Record<ShipmentStatusGroup, string> = {
  new: 'bg-[#E9F7EF]',
  created: 'bg-[#E7F3FF]',
  in_transit: 'bg-[#FFF5CC]',
  ready_to_collect: 'bg-[#E0F7FA]',
  delivered: 'bg-[#E8F5E9]',
  exception: 'bg-[#FFE9D6]',
  failed: 'bg-[#FDECEA]',
  returned: 'bg-[#F2E6FF]',
};

const badgeClasses =
  'inline-flex h-6 items-center justify-center gap-1 whitespace-nowrap rounded-[10px] px-2 text-sm font-medium leading-5';

/**
 * Shipment status chip. The tint comes from the colour-coding `group`; the label is
 * the localized title of the specific event `code` (e.g. `EOL.1001` -> "Delivered"),
 * falling back to the group title when no code is given (orders without a shipment).
 */
export function StatusBadge({
  group,
  code,
  className,
}: {
  group: ShipmentStatusGroup;
  code?: string | null;
  className?: string;
}) {
  const { t } = useTranslation('shipments');

  const label = code
    ? t(`eventCodes.${eventCodeKey(code)}`, { defaultValue: code })
    : t(`statusGroups.${group}`);

  return (
    <span className={cn(badgeClasses, 'text-black', GROUP_BG[group] ?? 'bg-[#F7F7F7]', className)}>
      {label}
    </span>
  );
}

const FINANCIAL_STATUS_STYLE: Record<FinancialStatus, string> = {
  paid: 'bg-[#E8F5E9] text-[#1B5E20]',
  pending: 'bg-[#F7F7F7] text-black',
  refunded: 'bg-[#F2E6FF] text-black',
  partially_refunded: 'bg-[#F2E6FF] text-black',
  voided: 'bg-[#FDECEA] text-black',
};

/** Payment status chip used next to the order title (figma "Paid"). */
export function FinancialStatusBadge({
  status,
  className,
}: {
  status: FinancialStatus;
  className?: string;
}) {
  const { t } = useTranslation('orders');

  return (
    <span
      className={cn(
        badgeClasses,
        FINANCIAL_STATUS_STYLE[status] ?? 'bg-[#F7F7F7] text-black',
        className,
      )}
    >
      {t(`financialStatuses.${status}`)}
    </span>
  );
}
